import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useInView, useSpring, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Play, X, Sparkles, Rocket, Compass, Users, TrendingUp, Building2,
  BrainCircuit, Shield, BarChart3, Radio, GraduationCap, Handshake, Wallet,
  Layers, Zap, Globe2, Check, ChevronDown,
} from "lucide-react";
import { FounderOSMark, FounderOSWordmark } from "../components/founderos/FounderOSMark";
import {
  DashboardScreen, VentureScreen, LearnScreen, ConnectScreen, AICopilotScreen,
  RaiseScreen, ScaleScreen, FounderScoreScreen, CollegeDashboardMini,
  ParentMiniCard, MentorMiniCard, InvestorMiniCard, MobileWidget,
} from "../components/founderos/ProductScreens";

const APP_URL = "https://app.bowerschool.com/";
const YT_ID = "im3_wq5nDx8";

// ==============================================================
// Design system primitives
// ==============================================================
const easeOut = [0.16, 1, 0.3, 1];
const stagger = { staggerChildren: 0.08 };
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: easeOut } } };
const fadeIn = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 1.2, ease: easeOut } } };

/** Section wrapper with reveal-on-scroll */
const Section = ({ id, className = "", children, dark = false }) => (
  <section
    id={id}
    className={`relative w-full ${dark ? "bg-[#04040a]" : ""} ${className}`}
  >
    {children}
  </section>
);

/** Glass card */
const Glass = ({ className = "", children, hoverLift = true, ...rest }) => (
  <div
    className={`relative rounded-2xl border border-white/10 backdrop-blur-xl overflow-hidden ${hoverLift ? "transition-all duration-500 hover:border-white/25 hover:-translate-y-1" : ""} ${className}`}
    style={{ background: "linear-gradient(155deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)" }}
    {...rest}
  >
    {children}
  </div>
);

/** Button variants — pill-shaped, Apple-like */
const Button = ({ variant = "primary", children, className = "", href, onClick, testid, ariaLabel }) => {
  const base = "inline-flex items-center gap-2 rounded-full text-sm md:text-[15px] font-medium tracking-tight px-5 md:px-6 py-2.5 md:py-3 transition-all duration-300 font-grotesk";
  const variants = {
    primary: "bg-white text-[#0a0a10] hover:bg-white/90 hover:shadow-[0_20px_60px_-10px_rgba(139,92,246,0.6)]",
    secondary: "bg-white/8 text-white border border-white/15 hover:bg-white/12 hover:border-white/25",
    accent: "text-white shadow-[0_20px_60px_-10px_rgba(139,92,246,0.6)] hover:shadow-[0_30px_80px_-10px_rgba(139,92,246,0.8)] bg-gradient-to-br from-[#4242FF] via-[#7C3AED] to-[#A855F7]",
  };
  const cls = `${base} ${variants[variant]} ${className}`;
  if (href) return <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className={cls} data-testid={testid} aria-label={ariaLabel}>{children}</a>;
  return <button onClick={onClick} className={cls} data-testid={testid} aria-label={ariaLabel}>{children}</button>;
};

/** Eyebrow chip that sits above every section headline */
const Eyebrow = ({ children, icon: Icon = Sparkles }) => (
  <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-white/5 backdrop-blur-md">
    <Icon size={12} className="text-[#A855F7]" />
    <span className="text-[11px] uppercase tracking-[0.18em] text-white/70 font-grotesk font-medium">{children}</span>
  </motion.div>
);

/** Big display headline */
const Display = ({ children, className = "" }) => (
  <motion.h2 variants={fadeUp} className={`font-display font-semibold text-white tracking-[-0.03em] leading-[1.02] ${className}`} style={{ fontVariationSettings: "'opsz' 96" }}>
    {children}
  </motion.h2>
);

/** Numeric count-up */
const CountUp = ({ to, suffix = "", duration = 2.2 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const step = (t) => {
      const p = Math.min((t - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(to * eased));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, to, duration]);
  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
};

// ==============================================================
// Sticky Nav
// ==============================================================
const Nav = ({ onWatchDemo }) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", s, { passive: true });
    return () => window.removeEventListener("scroll", s);
  }, []);
  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "bg-black/60 backdrop-blur-xl border-b border-white/[0.06]" : "bg-transparent"}`} data-testid="fos-nav">
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-3.5 flex items-center justify-between">
        <FounderOSWordmark />
        <nav className="hidden md:flex items-center gap-8 text-[13px] text-white/70 font-grotesk">
          <a href="#product" className="hover:text-white transition-colors">Product</a>
          <a href="#intelligence" className="hover:text-white transition-colors">Intelligence</a>
          <a href="#ecosystem" className="hover:text-white transition-colors">Ecosystem</a>
          <a href="#enterprise" className="hover:text-white transition-colors">Enterprise</a>
        </nav>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={onWatchDemo} testid="nav-demo-btn" ariaLabel="Watch demo" className="hidden sm:inline-flex">
            <Play size={12} /> Demo
          </Button>
          <Button variant="primary" href={APP_URL} testid="nav-cta-btn" ariaLabel="Open Founder OS">
            Get Founder OS <ArrowRight size={14} />
          </Button>
        </div>
      </div>
    </header>
  );
};

// ==============================================================
// 1. HERO
// ==============================================================
const Hero = ({ onWatchDemo }) => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 0.2], [0, -60]);
  const scale = useTransform(scrollYProgress, [0, 0.2], [1, 0.96]);
  const opacity = useTransform(scrollYProgress, [0, 0.15], [1, 0.6]);
  return (
    <Section id="hero" className="min-h-[100svh] pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden">
      {/* Ambient background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[#04040a]" />
        <div className="absolute inset-0 opacity-70" style={{ background: "radial-gradient(ellipse 60% 50% at 50% 0%, rgba(139,92,246,0.28), transparent 70%)" }} />
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(ellipse 40% 30% at 80% 40%, rgba(66,66,255,0.35), transparent 70%)" }} />
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-5 md:px-8 text-center">
        <motion.div initial="hidden" animate="visible" variants={stagger} className="flex flex-col items-center">
          <Eyebrow icon={Sparkles}>Introducing Founder OS</Eyebrow>
          <motion.h1
            variants={fadeUp}
            className="font-display font-semibold text-white tracking-[-0.035em] leading-[0.98] mt-6 mb-6"
            style={{ fontSize: "clamp(44px, 8vw, 110px)", fontVariationSettings: "'opsz' 96" }}
            data-testid="fos-hero-headline"
          >
            The Operating System
            <br />
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(135deg, #B794F6 0%, #7C3AED 40%, #4242FF 100%)" }}>
              for Every Entrepreneur.
            </span>
          </motion.h1>
          <motion.p variants={fadeUp} className="font-sans-fos text-white/60 text-base md:text-xl max-w-2xl leading-relaxed mb-9" style={{ fontWeight: 400 }}>
            One product. Every workflow a founder needs — building, learning, connecting, raising, and scaling — unified in a single beautifully designed operating system.
          </motion.p>
          <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-center gap-3">
            <Button variant="accent" href={APP_URL} testid="hero-cta-primary">
              Get Founder OS <ArrowRight size={16} />
            </Button>
            <Button variant="secondary" onClick={onWatchDemo} testid="hero-cta-secondary">
              <Play size={13} /> Watch Demo
            </Button>
          </motion.div>
        </motion.div>

        {/* Product hero — dashboard + two floating phones */}
        <motion.div style={{ y, scale, opacity }} className="relative mt-14 md:mt-20">
          <motion.div
            initial={{ opacity: 0, y: 80 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 1.2, ease: easeOut }}
            className="relative mx-auto max-w-[1180px]"
          >
            {/* Halo */}
            <div className="pointer-events-none absolute -inset-x-10 -inset-y-16 opacity-70" style={{ background: "radial-gradient(ellipse at center, rgba(139,92,246,0.35), transparent 60%)" }} />
            {/* Dashboard — native widget */}
            <div className="relative">
              <DashboardScreen />
            </div>
            {/* Left floating phone */}
            <motion.div
              initial={{ opacity: 0, x: -40, y: 40, rotate: -18 }}
              animate={{ opacity: 1, x: 0, y: 0, rotate: -10 }}
              transition={{ delay: 1, duration: 1.2, ease: easeOut }}
              className="hidden md:block absolute -left-8 lg:-left-16 top-1/2 -translate-y-1/2 w-[180px] lg:w-[220px]"
            >
              <PhoneFrame><MobileWidget variant="dashboard" /></PhoneFrame>
            </motion.div>
            {/* Right floating phone */}
            <motion.div
              initial={{ opacity: 0, x: 40, y: 40, rotate: 18 }}
              animate={{ opacity: 1, x: 0, y: 0, rotate: 10 }}
              transition={{ delay: 1.15, duration: 1.2, ease: easeOut }}
              className="hidden md:block absolute -right-8 lg:-right-16 top-1/2 -translate-y-1/2 w-[180px] lg:w-[220px]"
            >
              <PhoneFrame><MobileWidget variant="copilot" /></PhoneFrame>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </Section>
  );
};

/** iPhone-style device frame (accepts either src or children) */
const PhoneFrame = ({ src, children, className = "" }) => (
  <div className={`relative rounded-[32px] p-2 border border-white/15 bg-gradient-to-b from-white/[0.08] to-white/[0.02] backdrop-blur-md shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] ${className}`}>
    <div className="rounded-[24px] overflow-hidden bg-black aspect-[9/19.5] relative">
      {children ? children : <img src={src} alt="Founder OS mobile" className="w-full h-full object-cover" loading="lazy" />}
    </div>
    {/* notch */}
    <div className="absolute left-1/2 -translate-x-1/2 top-3 w-16 h-4 rounded-full bg-black z-10" />
  </div>
);

// ==============================================================
// 2. PROBLEM
// ==============================================================
const TOOL_ICONS = [
  { label: "CRM", icon: Users }, { label: "Docs", icon: Layers }, { label: "Learn", icon: GraduationCap },
  { label: "Chat", icon: Radio }, { label: "Deck", icon: BarChart3 }, { label: "Finance", icon: Wallet },
  { label: "Board", icon: Building2 }, { label: "Analytics", icon: TrendingUp }, { label: "AI", icon: BrainCircuit },
];

const Problem = () => {
  return (
    <Section id="problem" dark className="py-28 md:py-40 overflow-hidden">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <Eyebrow icon={Layers}>The Problem</Eyebrow>
            <Display className="mt-5 text-4xl md:text-5xl lg:text-[64px]">
              Entrepreneurs don&rsquo;t need
              <br />
              more tools.
              <br />
              <span className="text-white/40">They need one</span>
              <br />
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(135deg, #B794F6, #4242FF)" }}>operating system.</span>
            </Display>
            <motion.p variants={fadeUp} className="mt-6 text-white/60 text-lg leading-relaxed font-sans-fos max-w-lg">
              Nine subscriptions. Twelve tabs. Endless copy-paste between disconnected apps. The average founder loses <span className="text-white">14 hours a week</span> just switching context.
            </motion.p>
          </div>

          {/* Right — scattered → unified visualization */}
          <motion.div variants={fadeIn} className="relative h-[440px] md:h-[520px]">
            <ScatteredTools />
          </motion.div>
        </motion.div>
      </div>
    </Section>
  );
};

const ScatteredTools = () => {
  const [merged, setMerged] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMerged(true), 800);
    const iv = setInterval(() => setMerged((m) => !m), 4200);
    return () => { clearTimeout(t); clearInterval(iv); };
  }, []);
  // Positions of the scattered tool icons (randomish grid)
  const positions = [
    { x: -180, y: -140 }, { x: 40, y: -180 }, { x: 200, y: -100 },
    { x: -220, y: 20 }, { x: 220, y: 30 }, { x: -140, y: 160 },
    { x: 60, y: 180 }, { x: 200, y: 140 }, { x: -40, y: -60 },
  ];
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      {/* Center Founder OS mark */}
      <motion.div
        animate={{ scale: merged ? 1 : 0.85, opacity: merged ? 1 : 0.5 }}
        transition={{ duration: 0.8, ease: easeOut }}
        className="relative z-10"
      >
        <div className="relative">
          <div className="absolute -inset-8 rounded-full blur-2xl opacity-60" style={{ background: "radial-gradient(circle, #8B5CF6, transparent 70%)" }} />
          <FounderOSMark size={120} withGlow />
        </div>
      </motion.div>
      {TOOL_ICONS.map((tool, i) => {
        const { x, y } = positions[i];
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{
              opacity: merged ? 0 : 1,
              x: merged ? 0 : x,
              y: merged ? 0 : y,
              scale: merged ? 0.5 : 1,
            }}
            transition={{ duration: 1.2, ease: easeOut, delay: merged ? 0 : i * 0.05 }}
            className="absolute w-14 h-14 rounded-2xl border border-white/15 bg-white/[0.04] backdrop-blur-md flex items-center justify-center"
          >
            <tool.icon size={20} className="text-white/70" />
          </motion.div>
        );
      })}
    </div>
  );
};

// ==============================================================
// 3. MEET FOUNDER OS
// ==============================================================
const PILLARS = [
  { key: "build", title: "Build", desc: "Ventures, CRM, projects, tasks — everything a founder ships.", icon: Rocket },
  { key: "learn", title: "Learn", desc: "Courses, labs, journals, and lessons from top practitioners.", icon: GraduationCap },
  { key: "connect", title: "Connect", desc: "Community, mentors, co-founders, and events in one place.", icon: Handshake },
  { key: "raise", title: "Raise", desc: "Investor discovery, deck rooms, and check-in updates.", icon: Wallet },
  { key: "scale", title: "Scale", desc: "Marketplace, internships, alumni, and enterprise workflows.", icon: TrendingUp },
];

const Meet = () => (
  <Section id="product" dark className="py-24 md:py-32 overflow-hidden">
    <div className="max-w-[1400px] mx-auto px-5 md:px-8">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="flex flex-col items-center text-center max-w-3xl mx-auto">
        <Eyebrow icon={Compass}>Meet Founder OS</Eyebrow>
        <Display className="mt-5 text-4xl md:text-5xl lg:text-[68px]">
          Five surfaces.
          <br />
          <span className="text-white/40">One product.</span>
        </Display>
        <motion.p variants={fadeUp} className="mt-6 text-white/60 text-base md:text-lg leading-relaxed font-sans-fos max-w-xl">
          Every workflow a founder needs, in a single interface. Beautifully designed. Deeply integrated. Ready on Day 1.
        </motion.p>
      </motion.div>

      {/* Hero dashboard — native widget */}
      <motion.div
        initial={{ opacity: 0, y: 80 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1.1, ease: easeOut }}
        className="relative mt-14 md:mt-20 max-w-[1180px] mx-auto"
      >
        <div className="pointer-events-none absolute -inset-x-10 -inset-y-16 opacity-70" style={{ background: "radial-gradient(ellipse at center, rgba(66,66,255,0.28), transparent 60%)" }} />
        <div className="relative">
          <DashboardScreen />
        </div>
      </motion.div>

      {/* Pillar cards */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        variants={stagger}
        className="mt-16 md:mt-20 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4"
      >
        {PILLARS.map((p) => (
          <motion.a key={p.key} href={`#${p.key}`} variants={fadeUp}>
            <Glass className="p-5 md:p-6 h-full group cursor-pointer" data-testid={`pillar-${p.key}`}>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#4242FF]/40 to-[#7C3AED]/40 border border-white/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <p.icon size={18} className="text-white" />
              </div>
              <h3 className="font-display font-semibold text-white text-lg md:text-xl mb-1.5 tracking-tight">{p.title}</h3>
              <p className="text-white/50 text-[13px] leading-relaxed font-sans-fos">{p.desc}</p>
              <div className="mt-4 flex items-center gap-1.5 text-[12px] font-grotesk text-white/40 group-hover:text-white transition-colors">
                Explore <ArrowRight size={11} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </Glass>
          </motion.a>
        ))}
      </motion.div>
    </div>
  </Section>
);

// ==============================================================
// Reusable Product Section (Build / Learn / Connect / Raise / Scale)
// ==============================================================
const ProductSection = ({ id, eyebrow, icon, title, description, widget, reverse = false, accent = "#8B5CF6", secondaryTitle }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);
  return (
    <Section id={id} dark className="py-24 md:py-36 overflow-hidden">
      <div ref={ref} className="max-w-[1400px] mx-auto px-5 md:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
          className={`grid grid-cols-1 lg:grid-cols-[1fr_1.35fr] gap-12 lg:gap-16 items-center ${reverse ? "lg:[&>div:first-child]:order-2" : ""}`}
        >
          <div>
            <Eyebrow icon={icon}>{eyebrow}</Eyebrow>
            <Display className="mt-5 text-4xl md:text-5xl lg:text-[60px]">
              {title}
              {secondaryTitle && <><br /><span className="bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(135deg, ${accent}, #4242FF)` }}>{secondaryTitle}</span></>}
            </Display>
            <motion.p variants={fadeUp} className="mt-6 text-white/60 text-base md:text-lg leading-relaxed font-sans-fos max-w-lg">
              {description}
            </motion.p>
          </div>

          <motion.div style={{ y }} className="relative">
            <div className="pointer-events-none absolute -inset-10 opacity-70 blur-3xl" style={{ background: `radial-gradient(circle at 50% 50%, ${accent}55, transparent 60%)` }} />
            <div className="relative">{widget}</div>
          </motion.div>
        </motion.div>
      </div>
    </Section>
  );
};

// ==============================================================
// 7. AI COPILOT (hero)
// ==============================================================
const AICopilot = () => (
  <Section id="ai" dark className="py-28 md:py-40 overflow-hidden">
    <div className="max-w-[1400px] mx-auto px-5 md:px-8">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="text-center max-w-3xl mx-auto">
        <Eyebrow icon={BrainCircuit}>AI Copilot</Eyebrow>
        <Display className="mt-5 text-4xl md:text-5xl lg:text-[76px]">
          Your co-founder,
          <br />
          <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(135deg, #B794F6, #4242FF)" }}>always awake.</span>
        </Display>
        <motion.p variants={fadeUp} className="mt-6 text-white/60 text-base md:text-lg leading-relaxed font-sans-fos max-w-xl mx-auto">
          Ask anything. Draft pitches. Review decks. Analyze cohorts. Founder OS learns from every action you take across the OS.
        </motion.p>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, scale: 0.94, y: 60 }}
        whileInView={{ opacity: 1, scale: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1.2, ease: easeOut }}
        className="relative mt-14 md:mt-20 max-w-[1100px] mx-auto"
      >
        <div className="pointer-events-none absolute -inset-x-16 -inset-y-16 opacity-80 blur-3xl" style={{ background: "radial-gradient(ellipse at center, rgba(139,92,246,0.4), rgba(66,66,255,0.15) 40%, transparent 70%)" }} />
        <div className="relative">
          <AICopilotScreen />
        </div>
      </motion.div>
    </div>
  </Section>
);

// ==============================================================
// 10. COMMUNICATION PLATFORM (Architecture visualization)
// ==============================================================
const CommsPlatform = () => (
  <Section id="comms" dark className="py-28 md:py-40 overflow-hidden">
    <div className="max-w-[1400px] mx-auto px-5 md:px-8">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <Eyebrow icon={Radio}>Communication Platform</Eyebrow>
          <Display className="mt-5 text-4xl md:text-5xl lg:text-[64px]">
            Every message.
            <br />
            <span className="text-white/40">Every channel.</span>
            <br />
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(135deg, #B794F6, #4242FF)" }}>One thread.</span>
          </Display>
          <motion.p variants={fadeUp} className="mt-6 text-white/60 text-base md:text-lg leading-relaxed font-sans-fos max-w-lg">
            Real-time chat, DMs, community channels, mentor updates, investor rooms, and school-wide broadcasts — all routed through a unified events layer with built-in AI moderation.
          </motion.p>
          <motion.div variants={fadeUp} className="mt-6 flex flex-wrap gap-2">
            {["Realtime", "Multi-tenant", "Encrypted", "AI-moderated", "Webhook-native"].map((t) => (
              <span key={t} className="text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/70 font-grotesk">{t}</span>
            ))}
          </motion.div>
        </div>
        <ArchDiagram />
      </motion.div>
    </div>
  </Section>
);

const ArchDiagram = () => {
  const nodes = [
    { x: 50, y: 15, label: "Broadcast", icon: Radio },
    { x: 85, y: 40, label: "Mentors", icon: Handshake },
    { x: 75, y: 78, label: "Investors", icon: Wallet },
    { x: 25, y: 78, label: "Community", icon: Users },
    { x: 15, y: 40, label: "AI Router", icon: BrainCircuit },
  ];
  return (
    <motion.div variants={fadeIn} className="relative aspect-square w-full max-w-[560px] mx-auto">
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
        <defs>
          <linearGradient id="line-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#4242FF" stopOpacity="0.1" />
          </linearGradient>
        </defs>
        {nodes.map((n, i) => (
          <line key={i} x1="50" y1="50" x2={n.x} y2={n.y} stroke="url(#line-grad)" strokeWidth="0.3" strokeDasharray="1 1" />
        ))}
      </svg>
      {/* Center hub */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <div className="relative">
          <div className="absolute -inset-6 rounded-full blur-2xl opacity-60" style={{ background: "radial-gradient(circle, #8B5CF6, transparent 70%)" }} />
          <div className="relative w-24 h-24 md:w-28 md:h-28 rounded-3xl bg-gradient-to-br from-[#4242FF] to-[#7C3AED] flex items-center justify-center border border-white/20 shadow-2xl">
            <FounderOSMark size={54} />
          </div>
        </div>
      </div>
      {/* Nodes */}
      {nodes.map((n, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 0.6 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: easeOut, delay: 0.2 + i * 0.1 }}
          className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1.5"
          style={{ left: `${n.x}%`, top: `${n.y}%` }}
        >
          <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-white/[0.06] border border-white/10 backdrop-blur-md flex items-center justify-center">
            <n.icon size={20} className="text-white/85" />
          </div>
          <span className="text-[11px] uppercase tracking-widest text-white/60 font-grotesk">{n.label}</span>
        </motion.div>
      ))}
    </motion.div>
  );
};

// ==============================================================
// 11. UNIFIED INTELLIGENCE
// ==============================================================
const Intelligence = () => (
  <Section id="intelligence" dark className="py-24 md:py-32 overflow-hidden">
    <div className="max-w-[1400px] mx-auto px-5 md:px-8">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="text-center max-w-3xl mx-auto">
        <Eyebrow icon={BarChart3}>Unified Intelligence</Eyebrow>
        <Display className="mt-5 text-4xl md:text-5xl lg:text-[68px]">
          One truth.
          <br />
          <span className="text-white/40">Every stakeholder.</span>
        </Display>
        <motion.p variants={fadeUp} className="mt-6 text-white/60 text-base md:text-lg leading-relaxed font-sans-fos max-w-xl mx-auto">
          Executive dashboards for founders, schools, mentors, parents, and investors — driven by the same event stream. The Founder Score turns activity into signal.
        </motion.p>
      </motion.div>
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        variants={stagger}
        className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5"
      >
        {[
          { title: "Founder Score", widget: <FounderScoreScreen />, span: "lg:col-span-2 lg:row-span-2" },
          { title: "College Dashboard", widget: <CollegeDashboardMini /> },
          { title: "Parent Dashboard", widget: <ParentMiniCard /> },
          { title: "Mentor Dashboard", widget: <MentorMiniCard /> },
          { title: "Investor Dashboard", widget: <InvestorMiniCard /> },
        ].map((c, i) => (
          <motion.div key={i} variants={fadeUp} className={c.span}>
            <Glass className="overflow-hidden h-full">
              <div className="p-4 md:p-5 border-b border-white/5 flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-widest text-white/50 font-grotesk">{c.title}</span>
                <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
              </div>
              <div className="bg-[#0b0b12]">{c.widget}</div>
            </Glass>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </Section>
);

// ==============================================================
// 12. MOBILE EXPERIENCE
// ==============================================================
const MobileExperience = () => (
  <Section id="mobile" dark className="py-28 md:py-40 overflow-hidden">
    <div className="max-w-[1400px] mx-auto px-5 md:px-8">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="text-center max-w-3xl mx-auto">
        <Eyebrow icon={Sparkles}>Mobile Experience</Eyebrow>
        <Display className="mt-5 text-4xl md:text-5xl lg:text-[68px]">
          A founder&rsquo;s OS,
          <br />
          <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(135deg, #B794F6, #4242FF)" }}>in your pocket.</span>
        </Display>
      </motion.div>
      <div className="mt-14 md:mt-20 relative">
        <div className="pointer-events-none absolute inset-0 opacity-60" style={{ background: "radial-gradient(ellipse at center, rgba(139,92,246,0.25), transparent 60%)" }} />
        <div className="relative grid grid-cols-3 md:grid-cols-5 gap-3 md:gap-5 max-w-[1100px] mx-auto">
          {["dashboard", "copilot", "score", "community", "learn"].map((variant, i) => (
            <motion.div
              key={variant}
              initial={{ opacity: 0, y: 60, rotate: i % 2 === 0 ? -4 : 4 }}
              whileInView={{ opacity: 1, y: [(i - 2) * 6, (i - 2) * -6, (i - 2) * 6], rotate: i % 2 === 0 ? -2 : 2 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                opacity: { duration: 0.8, ease: easeOut, delay: i * 0.12 },
                y: { duration: 5 + i * 0.3, repeat: Infinity, ease: "easeInOut" },
                rotate: { duration: 0.8, ease: easeOut, delay: i * 0.12 },
              }}
              style={{ marginTop: i % 2 === 0 ? 0 : "40px" }}
            >
              <PhoneFrame><MobileWidget variant={variant} /></PhoneFrame>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </Section>
);

// ==============================================================
// 13. ECOSYSTEM
// ==============================================================
const ECOSYSTEM = [
  { label: "School OS", icon: Building2 },
  { label: "University OS", icon: GraduationCap },
  { label: "Student App", icon: Rocket },
  { label: "Mentor Portal", icon: Handshake },
  { label: "Investor Portal", icon: Wallet },
  { label: "Parent Portal", icon: Users },
  { label: "Enterprise", icon: Shield },
  { label: "AI Copilot", icon: BrainCircuit },
  { label: "Analytics", icon: BarChart3 },
  { label: "Communication Platform", icon: Radio },
];

const Ecosystem = () => (
  <Section id="ecosystem" dark className="py-28 md:py-40 overflow-hidden">
    <div className="max-w-[1400px] mx-auto px-5 md:px-8">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="text-center max-w-3xl mx-auto">
        <Eyebrow icon={Globe2}>Founder OS Ecosystem</Eyebrow>
        <Display className="mt-5 text-4xl md:text-5xl lg:text-[68px]">
          Ten surfaces.
          <br />
          <span className="text-white/40">One platform.</span>
        </Display>
      </motion.div>
      <div className="relative mt-16 md:mt-24 aspect-square max-w-[720px] mx-auto">
        {/* Concentric rings */}
        {[0.45, 0.65, 0.85].map((r, i) => (
          <div
            key={i}
            className="absolute rounded-full border border-white/[0.06]"
            style={{
              inset: `${(1 - r) * 50}%`,
              boxShadow: `0 0 0 1px rgba(139,92,246,${0.03 + i * 0.02}), inset 0 0 40px rgba(139,92,246,${0.02})`,
            }}
          />
        ))}
        {/* Center */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
            className="absolute -inset-10 rounded-full opacity-40"
            style={{ background: "conic-gradient(from 0deg, transparent, rgba(139,92,246,0.4), transparent 30%)" }}
          />
          <div className="relative">
            <div className="absolute -inset-8 rounded-full blur-2xl opacity-70" style={{ background: "radial-gradient(circle, #8B5CF6, transparent 70%)" }} />
            <FounderOSMark size={140} withGlow />
          </div>
        </div>
        {/* Orbiting nodes */}
        {ECOSYSTEM.map((item, i) => {
          const angle = (i / ECOSYSTEM.length) * Math.PI * 2 - Math.PI / 2;
          const radius = 42;
          const x = 50 + Math.cos(angle) * radius;
          const y = 50 + Math.sin(angle) * radius;
          return (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: easeOut, delay: 0.15 + i * 0.06 }}
              className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1.5"
              style={{ left: `${x}%`, top: `${y}%` }}
            >
              <div className="relative">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-white/[0.06] border border-white/10 backdrop-blur-md flex items-center justify-center hover:border-white/25 hover:bg-white/10 transition-all">
                  <item.icon size={20} className="text-white/85" />
                </div>
              </div>
              <span className="text-[10px] md:text-[11px] uppercase tracking-widest text-white/60 font-grotesk text-center whitespace-nowrap">{item.label}</span>
            </motion.div>
          );
        })}
      </div>
    </div>
  </Section>
);

// ==============================================================
// 14. ENTERPRISE READY
// ==============================================================
const ENTERPRISE = [
  { icon: Layers, title: "Multi-Tenant", desc: "Isolated data per school, cohort, or enterprise. Row-level policies out of the box." },
  { icon: BrainCircuit, title: "AI Layer", desc: "Native LLM integration across every workflow with pluggable model providers." },
  { icon: Shield, title: "Security", desc: "SOC-ready. SSO. Role-based access. Audit logs. Encryption at rest & in transit." },
  { icon: BarChart3, title: "Analytics", desc: "Warehouse-native event stream. Custom dashboards per stakeholder." },
  { icon: Radio, title: "Communication", desc: "Real-time messaging & broadcasts with webhook-native integrations." },
  { icon: Zap, title: "Extensibility", desc: "REST + GraphQL APIs. First-class SDKs. Build on top of Founder OS." },
];

const Enterprise = () => (
  <Section id="enterprise" dark className="py-28 md:py-40 overflow-hidden">
    <div className="max-w-[1400px] mx-auto px-5 md:px-8">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="text-center max-w-3xl mx-auto">
        <Eyebrow icon={Shield}>Enterprise Ready</Eyebrow>
        <Display className="mt-5 text-4xl md:text-5xl lg:text-[68px]">
          Built for scale.
          <br />
          <span className="text-white/40">Ready on Day 1.</span>
        </Display>
      </motion.div>
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
        {ENTERPRISE.map((e, i) => (
          <motion.div key={i} variants={fadeUp}>
            <Glass className="p-6 md:p-7 h-full">
              <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-4">
                <e.icon size={18} className="text-white" />
              </div>
              <h3 className="font-display font-semibold text-white text-lg md:text-xl mb-2 tracking-tight">{e.title}</h3>
              <p className="text-white/55 text-sm leading-relaxed font-sans-fos">{e.desc}</p>
            </Glass>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </Section>
);

// ==============================================================
// 15. TRUSTED BY BUILDERS
// ==============================================================
const METRICS = [
  { value: 12000, suffix: "+", label: "Student Entrepreneurs" },
  { value: 240, suffix: "+", label: "Schools & Universities" },
  { value: 850, suffix: "+", label: "Mentors" },
  { value: 3400, suffix: "+", label: "Ventures Launched" },
  { value: 18, suffix: "", label: "Countries" },
];

const TrustedBy = () => (
  <Section id="trusted" dark className="py-28 md:py-40 overflow-hidden">
    <div className="max-w-[1400px] mx-auto px-5 md:px-8">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
        <Eyebrow icon={Users}>Trusted by Builders</Eyebrow>
        <Display className="mt-5 text-4xl md:text-5xl lg:text-[68px]">
          The largest founder
          <br />
          <span className="text-white/40">network on Earth.</span>
        </Display>
      </motion.div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8">
        {METRICS.map((m, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: easeOut, delay: i * 0.08 }}
            className="text-center"
          >
            <div className="font-display font-semibold text-white tracking-[-0.04em] leading-none" style={{ fontSize: "clamp(40px, 6vw, 92px)", fontVariationSettings: "'opsz' 96" }}>
              <CountUp to={m.value} suffix={m.suffix} />
            </div>
            <div className="mt-3 text-[11px] md:text-[13px] uppercase tracking-widest text-white/50 font-grotesk">{m.label}</div>
          </motion.div>
        ))}
      </div>
    </div>
  </Section>
);

// ==============================================================
// 16. VISION
// ==============================================================
const Vision = () => (
  <Section id="vision" dark className="py-40 md:py-56 overflow-hidden">
    <div className="max-w-[1200px] mx-auto px-5 md:px-8 text-center">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
        <motion.h2
          variants={fadeUp}
          className="font-display font-semibold text-white tracking-[-0.04em] leading-[1.05]"
          style={{ fontSize: "clamp(48px, 9vw, 128px)", fontVariationSettings: "'opsz' 96" }}
        >
          Every entrepreneur
          <br />
          <span className="text-white/50">deserves an</span>
          <br />
          <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(135deg, #B794F6, #4242FF)" }}>operating system.</span>
        </motion.h2>
        <motion.div variants={fadeUp} className="mt-14 flex items-center justify-center gap-2.5">
          <FounderOSMark size={28} withGlow />
          <span className="font-display text-white text-lg tracking-tight font-medium" style={{ fontVariationSettings: "'opsz' 24" }}>Founder<span className="text-white/60 ml-1 font-normal">OS</span></span>
        </motion.div>
      </motion.div>
    </div>
  </Section>
);

// ==============================================================
// 17. FOOTER
// ==============================================================
const Footer = ({ onWatchDemo }) => (
  <footer className="relative bg-[#04040a] border-t border-white/[0.06] pt-24 pb-10 overflow-hidden" data-testid="fos-footer">
    <div className="absolute inset-0 opacity-60 pointer-events-none" style={{ background: "radial-gradient(ellipse 50% 60% at 50% 0%, rgba(139,92,246,0.18), transparent 70%)" }} />
    <div className="relative max-w-[1200px] mx-auto px-5 md:px-8">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="text-center max-w-2xl mx-auto mb-16">
        <Display className="text-4xl md:text-5xl lg:text-[64px]">
          Download Founder OS.
        </Display>
        <motion.p variants={fadeUp} className="mt-5 text-white/60 text-base md:text-lg font-sans-fos">Everything a founder needs — in one product.</motion.p>
        <motion.div variants={fadeUp} className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Button variant="accent" href={APP_URL} testid="footer-cta-primary">Get Founder OS <ArrowRight size={16} /></Button>
          <Button variant="secondary" onClick={onWatchDemo} testid="footer-cta-secondary"><Play size={13} /> Watch Demo</Button>
        </motion.div>
      </motion.div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 py-10 border-t border-white/[0.06]">
        <div className="col-span-2 md:col-span-1">
          <FounderOSWordmark />
          <p className="mt-3 text-white/50 text-sm max-w-xs font-sans-fos leading-relaxed">The Operating System for every entrepreneur — built by Bower.</p>
        </div>
        {[
          { title: "Product", items: [["Build", "#build"], ["Learn", "#learn"], ["Connect", "#connect"], ["Raise", "#raise"], ["Scale", "#scale"]] },
          { title: "Platform", items: [["AI Copilot", "#ai"], ["Intelligence", "#intelligence"], ["Ecosystem", "#ecosystem"], ["Enterprise", "#enterprise"]] },
          { title: "Company", items: [["Contact", "mailto:hello@bowerschool.com"], ["Terms", "https://bowerschool.com/terms-of-service"], ["Privacy", "https://bowerschool.com/privacy-policy"], ["Twitter / X", "#"], ["LinkedIn", "#"]] },
        ].map((col) => (
          <div key={col.title}>
            <h4 className="text-[11px] uppercase tracking-widest text-white/40 font-grotesk font-semibold mb-4">{col.title}</h4>
            <ul className="flex flex-col gap-2.5">
              {col.items.map(([label, href]) => (
                <li key={label}>
                  <a href={href} target={href.startsWith("http") || href.startsWith("mailto") ? "_blank" : undefined} rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors text-sm font-sans-fos">{label}</a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="pt-6 border-t border-white/[0.06] flex items-center justify-between flex-wrap gap-2">
        <p className="text-white/40 text-xs font-sans-fos">&copy; {new Date().getFullYear()} Bower School of Entrepreneurship. All rights reserved.</p>
        <p className="text-white/30 text-xs font-grotesk uppercase tracking-widest">Designed &amp; engineered in Hyderabad</p>
      </div>
    </div>
  </footer>
);

// ==============================================================
// Demo Modal (shared)
// ==============================================================
const DemoModal = ({ open, onClose }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
        onClick={onClose}
        data-testid="fos-demo-modal"
      >
        <button onClick={onClose} aria-label="Close demo" className="absolute top-5 right-5 md:top-8 md:right-8 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 flex items-center justify-center text-white transition-colors">
          <X size={20} />
        </button>
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.94 }}
          transition={{ duration: 0.4, ease: easeOut }}
          className="w-full max-w-6xl aspect-video rounded-2xl overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <iframe
            className="w-full h-full"
            src={`https://www.youtube.com/embed/${YT_ID}?autoplay=1&rel=0`}
            title="Founder OS Demo"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ==============================================================
// PAGE
// ==============================================================
export default function FounderOSPage() {
  const [demoOpen, setDemoOpen] = useState(false);
  const openDemo = () => setDemoOpen(true);
  const closeDemo = () => setDemoOpen(false);

  // Set page title + meta on mount
  useEffect(() => {
    const prev = document.title;
    document.title = "Founder OS — The Operating System for Every Entrepreneur";
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="min-h-screen bg-[#04040a] text-white font-sans-fos overflow-x-hidden selection:bg-[#8B5CF6]/40" data-testid="fos-page">
      <Nav onWatchDemo={openDemo} />

      <main>
        <Hero onWatchDemo={openDemo} />
        <Problem />
        <Meet />

        <ProductSection
          id="build" eyebrow="Build" icon={Rocket}
          title="Ship your venture." secondaryTitle="From idea to invoice."
          accent="#8B5CF6"
          description="A native venture workspace with CRM, projects, tasks, and pipelines. Every artefact — from your first pitch to your first invoice — lives here."
          widget={<VentureScreen />}
        />
        <ProductSection
          id="learn" eyebrow="Learn" icon={GraduationCap}
          title="Learn by doing." secondaryTitle="Not by watching."
          reverse accent="#7C3AED"
          description="Practitioner-led courses, interactive labs, weekly journals, and structured cohorts. Every module ends with a real deliverable, reviewed by mentors."
          widget={<LearnScreen />}
        />
        <ProductSection
          id="connect" eyebrow="Connect" icon={Handshake}
          title="Find your people." secondaryTitle="Find your co-founder."
          accent="#6366F1"
          description="A vetted community of founders, mentors, and co-founder candidates — with events, DMs, and matching built on top of the same graph."
          widget={<ConnectScreen />}
        />

        <AICopilot />

        <ProductSection
          id="raise" eyebrow="Raise" icon={Wallet}
          title="Raise from the right room." secondaryTitle=""
          reverse accent="#4242FF"
          description="Investor discovery. Deck rooms. Update cadences. Founder OS turns your unstructured hustle into an investor-ready narrative — automatically."
          widget={<RaiseScreen />}
        />
        <ProductSection
          id="scale" eyebrow="Scale" icon={TrendingUp}
          title="Grow past the seed." secondaryTitle=""
          accent="#A855F7"
          description="Marketplace tools, internships, alumni network, and enterprise-grade workflows for teams as they cross Series A."
          widget={<ScaleScreen />}
        />

        <CommsPlatform />
        <Intelligence />
        <MobileExperience />
        <Ecosystem />
        <Enterprise />
        <TrustedBy />
        <Vision />
      </main>

      <Footer onWatchDemo={openDemo} />
      <DemoModal open={demoOpen} onClose={closeDemo} />
    </div>
  );
}
